/**
 * <s-sticky-atc> — sticky add-to-cart bar for the product page.
 *
 * Shows once the main product form has scrolled above the viewport and hides
 * again when it comes back into view. The bar's own form is wrapped in
 * <s-product-form>, so Add to cart uses the same on-page cart update and drawer.
 *
 * Markup:
 *   [data-sticky-variant]   — <select> of variants; each <option> carries data-price,
 *                             data-image and data-available
 *   input[name="id"]        — hidden variant id submitted by the form
 *   [data-sticky-price]     — price text
 *   [data-sticky-image]     — thumbnail <img>
 *   button[name="add"]      — submit button
 *   data-watch="<selector>" — the element whose visibility toggles the bar
 */
class SStickyAtc extends HTMLElement {
  #observer;
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.select = this.querySelector('[data-sticky-variant]');
    this.idInput = this.querySelector('input[name="id"]');
    this.select?.addEventListener('change', () => this.#apply(this.select.value), opts);

    const watched = document.querySelector(this.dataset.watch || '[data-product-form]');
    if (!watched) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.#setVisible(scrolledPast);
    });
    this.#observer.observe(watched);
    this.#setVisible(false);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#ac?.abort();
  }

  #setVisible(visible) {
    if (visible) {
      this.#syncFromMainForm();
      this.setAttribute('data-visible', '');
      this.inert = false;
    } else {
      this.removeAttribute('data-visible');
      this.inert = true;
    }
  }

  // Match the variant the shopper already picked in the main form.
  #syncFromMainForm() {
    const mainId = document.querySelector('[data-product-form] input[name="id"]')?.value;
    if (!mainId || !this.select) return;
    if ([...this.select.options].some((option) => option.value === mainId)) {
      this.select.value = mainId;
      this.#apply(mainId);
    }
  }

  #apply(variantId) {
    if (this.idInput) this.idInput.value = variantId;
    const option = this.select?.selectedOptions[0];
    if (!option) return;

    const price = this.querySelector('[data-sticky-price]');
    if (price && option.dataset.price) price.textContent = option.dataset.price;

    const image = this.querySelector('[data-sticky-image]');
    if (image && option.dataset.image) {
      image.src = option.dataset.image;
      image.removeAttribute('srcset');
    }

    const button = this.querySelector('button[name="add"]');
    if (button) {
      const available = option.dataset.available === 'true';
      button.toggleAttribute('disabled', !available);
      button.classList.toggle('opacity-50', !available);
      const label = available ? button.dataset.labelAdd : button.dataset.labelSoldOut;
      if (label) button.textContent = label;
    }
  }
}

if (!customElements.get('s-sticky-atc')) {
  customElements.define('s-sticky-atc', SStickyAtc);
}
